import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FiAlertCircle } from 'react-icons/fi'
import { useAuth } from 'context/auth'

const PREVIEW_STATES = ['past_due', 'canceling', 'canceled']

const toDate = (value) => {
  if (!value) return null
  const raw = typeof value === 'number' && value < 1e12 ? value * 1000 : value
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (date) =>
  date
    ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : 'at the end of this period'

const resolveState = (account, preview) => {
  if (preview) return preview
  const subscription = account?.subscription
  if (!subscription) return null
  const status = String(subscription.subscriptionStatus || subscription.status || '').toLowerCase()
  if (status === 'past_due') return 'past_due'
  if (status === 'canceled' || status === 'cancelled') {
    return account.plan !== 'free' ? 'canceled' : null
  }
  if (subscription.cancelAtPeriodEnd || status === 'canceling') return 'canceling'
  return null
}

const buildBanner = (state, account) => {
  switch (state) {
    case 'past_due':
      return {
        title: 'Payment failed',
        message: 'Update your payment method to keep your cards running.',
        action: { label: 'Update payment', to: '/subscribe' },
      }
    case 'canceling': {
      const subscription = account?.subscription || {}
      const endsAt = toDate(subscription.expiresAt) || toDate(subscription.nextPaymentDate)
      return {
        title: `Subscription ends ${formatDate(endsAt)}`,
        message: '',
        action: { label: 'Resume', to: '/subscribe' },
      }
    }
    case 'canceled':
      return {
        title: 'Subscription canceled',
        message: 'Reactivate your subscription to continue.',
        action: { label: 'Reactivate', to: '/subscribe' },
      }
    default:
      return null
  }
}

function AccountBanner({ message, action }) {
  const { currentUser } = useAuth()
  const location = useLocation()

  let preview = null
  if (process.env.NODE_ENV !== 'production') {
    const requested = new URLSearchParams(location.search).get('bannerPreview')
    if (PREVIEW_STATES.includes(requested)) preview = requested
  }

  const account = currentUser?.account
  const banner = message
    ? { title: message, message: '', action }
    : buildBanner(resolveState(account, preview), account)

  if (!banner) return null

  return (
    <div
      role='status'
      className='tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-bg-dangerSoft tw-px-5 tw-py-2.5 tw-text-[14px] tw-text-danger'
    >
      <FiAlertCircle aria-hidden='true' className='tw-h-4 tw-w-4 tw-shrink-0' />
      <span className='tw-font-semibold'>{banner.title}</span>
      {banner.message && (
        <>
          <span aria-hidden='true'>•</span>
          <span className='tw-min-w-0'>{banner.message}</span>
        </>
      )}
      {banner.action && (
        <Link
          to={banner.action.to}
          className='tw-ml-auto tw-whitespace-nowrap tw-rounded-[10px] tw-font-semibold tw-underline-offset-2 hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ember focus-visible:tw-ring-offset-2'
        >
          {banner.action.label} →
        </Link>
      )}
    </div>
  )
}

export default AccountBanner
